import { useState, useEffect, useRef, useMemo } from "react";
import { Send, Loader2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useClientSession } from "@/hooks/useClientSession";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { resolveAvatarUrl } from "@/components/ClientCadastroTab";
import OnlineIndicator from "@/components/OnlineIndicator";


interface PlatformChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  destinatarioId: string;
  destinatarioApelido: string;
  destinatarioAvatar?: string | null;
  anuncioId?: string;
  itemNome?: string;
}

interface Message {
  id: string;
  remetente_id: string;
  destinatario_id: string;
  conteudo: string;
  lida: boolean;
  created_at: string;
  anuncio_id?: string | null;
  anuncio_item_nome?: string | null;
}

const GENERAL_KEY = "__general__";

const PlatformChatDialog = ({
  open,
  onOpenChange,
  destinatarioId,
  destinatarioApelido,
  destinatarioAvatar,
  anuncioId,
  itemNome,
}: PlatformChatDialogProps) => {
  const { session } = useClientSession();
  const [messages, setMessages] = useState<Message[]>([]);
  const [myId, setMyId] = useState<string>("");
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(anuncioId || GENERAL_KEY);
  const [showOnlyCurrent, setShowOnlyCurrent] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<number | null>(null);
  const [lastOnlineAt, setLastOnlineAt] = useState<string | null>(null);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadMessages = async (silent = false) => {
    if (!session) return;
    if (!silent) setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("platform-messages", {
        body: {
          action: "list",
          contato: session.contato,
          token: session.token,
          conversa_com_id: destinatarioId,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setMessages((prev) => {
        const next = data.messages || [];
        // Avoid useless re-render if equal length and same last id
        if (
          silent &&
          prev.length === next.length &&
          prev[prev.length - 1]?.id === next[next.length - 1]?.id
        ) {
          return prev;
        }
        return next;
      });
      setMyId(data.my_id || "");
    } catch (err: any) {
      if (!silent) toast.error(err.message || "Erro ao carregar mensagens");
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    if (open && session) {
      setActiveTab(anuncioId || GENERAL_KEY);
      loadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session, destinatarioId, anuncioId]);

  // Polling fallback (anon users don't receive realtime due to RLS)
  useEffect(() => {
    if (!open || !session) return;
    pollingRef.current = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadMessages(true);
      }
    }, 4000);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session, destinatarioId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeTab]);

  // Fetch recipient's last_online_at for online indicator
  useEffect(() => {
    if (!open || !destinatarioId) return;
    let cancelled = false;
    const fetchPresence = async () => {
      try {
        const { data } = await supabase.functions.invoke("public-profile", {
          body: { perfilId: destinatarioId },
        });
        if (!cancelled && data?.success && data.profile) {
          setLastOnlineAt(data.profile.last_online_at || data.profile.updated_at || null);
        }
      } catch {
        // ignore
      }
    };
    fetchPresence();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") fetchPresence();
    }, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, destinatarioId]);


  // Realtime (works only if user is authenticated in Supabase; harmless otherwise)
  useEffect(() => {
    if (!open || !myId) return;
    const channel = supabase
      .channel(`platform-chat-${myId}-${destinatarioId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "mensagens_plataforma" },
        (payload) => {
          const msg = payload.new as Message;
          if (
            (msg.remetente_id === myId && msg.destinatario_id === destinatarioId) ||
            (msg.remetente_id === destinatarioId && msg.destinatario_id === myId)
          ) {
            // Reload to enrich with anuncio_item_nome
            loadMessages(true);
          }
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, myId, destinatarioId]);

  // Build tabs: one per distinct anuncio_id present in messages + current anuncioId if provided
  const tabs = useMemo(() => {
    const map = new Map<string, string>(); // key -> label
    // Include current ad context (so user can start a fresh ad-tab)
    if (anuncioId) map.set(anuncioId, itemNome || "Anúncio");
    for (const m of messages) {
      if (m.anuncio_id) {
        map.set(m.anuncio_id, m.anuncio_item_nome || "Anúncio");
      } else {
        map.set(GENERAL_KEY, "Geral");
      }
    }
    if (map.size === 0) map.set(GENERAL_KEY, "Geral");
    return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
  }, [messages, anuncioId, itemNome]);

  // Ensure activeTab exists in tabs
  useEffect(() => {
    if (!tabs.find((t) => t.key === activeTab)) {
      setActiveTab(tabs[0]?.key || GENERAL_KEY);
    }
  }, [tabs, activeTab]);

  const filteredMessages = useMemo(() => {
    return messages.filter((m) => {
      if (activeTab === GENERAL_KEY) return !m.anuncio_id;
      return m.anuncio_id === activeTab;
    });
  }, [messages, activeTab]);

  const handleSend = async () => {
    if (!newMessage.trim() || !session || sending) return;
    setSending(true);
    const content = newMessage.trim();
    setNewMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("platform-messages", {
        body: {
          action: "send",
          contato: session.contato,
          token: session.token,
          destinatario_id: destinatarioId,
          anuncio_id: activeTab === GENERAL_KEY ? null : activeTab,
          conteudo: content,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      // Refresh immediately
      loadMessages(true);
    } catch (err: any) {
      toast.error(err.message || "Erro ao enviar");
      setNewMessage(content);
    }
    setSending(false);
  };

  const avatarUrl = resolveAvatarUrl(destinatarioAvatar || null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tibia-border bg-card max-w-md p-0 gap-0 flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-3 border-b border-border/40 flex flex-col gap-2">
          <div className="flex items-center gap-2 pr-7">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center">
                <span className="font-pixel text-[7px] text-primary">
                  {destinatarioApelido.slice(0, 2).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-pixel text-[9px] text-primary truncate">{destinatarioApelido}</p>
              <OnlineIndicator lastOnlineAt={lastOnlineAt} />
            </div>

          </div>
          {tabs.length > 0 && (
            <label className="flex items-center gap-1.5 cursor-pointer self-start" title="Exibir apenas a aba atual">
              <Checkbox
                checked={showOnlyCurrent}
                onCheckedChange={(v) => setShowOnlyCurrent(!!v)}
                className="h-3 w-3"
              />
              <span className="font-pixel text-[7px] text-muted-foreground">Exibir apenas atual</span>
            </label>
          )}
        </div>

        {/* Tabs - one per advertised item from this contact */}
        {tabs.length > 0 && (

          <div className="flex gap-1 px-2 pt-2 border-b border-border/40 overflow-x-auto scrollbar-thin">
            {(showOnlyCurrent ? tabs.filter((t) => t.key === activeTab) : tabs).map((t) => {
              const isActive = t.key === activeTab;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex-shrink-0 px-2 py-1 rounded-t-sm text-[8px] font-pixel transition-colors whitespace-nowrap max-w-[140px] truncate ${
                    isActive
                      ? "bg-primary/20 text-primary border-b-2 border-primary"
                      : "bg-secondary/40 text-muted-foreground hover:bg-secondary"
                  }`}
                  title={t.label}
                >
                  {t.key === GENERAL_KEY ? "💬 Geral" : `📦 ${t.label}`}
                </button>
              );
            })}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[200px] max-h-[400px]">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[9px] text-muted-foreground">Nenhuma mensagem ainda.</p>
              <p className="text-[8px] text-muted-foreground mt-1">Envie a primeira mensagem!</p>
            </div>
          ) : (
            filteredMessages.map((msg) => {
              const isMine = msg.remetente_id === myId;
              return (
                <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-2.5 py-1.5 rounded-lg text-[9px] ${
                      isMine
                        ? "bg-primary/20 text-primary"
                        : "bg-secondary text-foreground"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{msg.conteudo}</p>
                    <p className={`text-[6px] mt-0.5 ${isMine ? "text-primary/50" : "text-muted-foreground"}`}>
                      {(() => {
                        try {
                          return format(new Date(msg.created_at), "dd/MM HH:mm", { locale: ptBR });
                        } catch {
                          return "";
                        }
                      })()}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-2 border-t border-border/40 flex items-center gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
            placeholder="Digite sua mensagem..."
            className="tibia-input rounded-sm text-[10px] flex-1 py-1.5"
            maxLength={1000}
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="tibia-btn rounded-sm p-2 disabled:opacity-40"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlatformChatDialog;
